import axios from "axios";
import { useEffect, useState } from "react";
import { Star } from "lucide-react";

interface Premio {
  premio_id: number;
  nombre: string;
  puntos?: number;
  imagen?: string;
  estado?: string;
  signedUrl?: string;
}

function formatPts(n: number) {
  return n.toLocaleString("es-CR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

const MAX_DESTACADOS = 6;

/** Elige hasta `n` premios al azar (sin repetir) de la lista. */
function pickNRandom<T>(list: T[], n: number): T[] {
  if (list.length <= n) {
    return [...list];
  }
  const shuffled = [...list];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled.slice(0, n);
}

/** Muestra varios premios destacados al azar (activos) — mismo origen que Premios. */
const TopPremiosTeaser = ({ onVerMas }: { onVerMas: () => void }) => {
  const [items, setItems] = useState<Premio[]>([]);

  useEffect(() => {
    const run = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/premios/activos`,
          {
            headers: { "auth-token": `${sessionStorage.getItem("token")}` },
          }
        );
        const raw = Array.isArray(response.data) ? (response.data as Premio[]) : [];
        const activos = raw.filter(
          (p) => (p.estado || "").toLowerCase() !== "inactivo"
        );
        const sample = pickNRandom(activos, MAX_DESTACADOS);
        const withUrls: Premio[] = await Promise.all(
          sample.map(async (p) => {
            if (!p.imagen) {
              return p;
            }
            try {
              const { data: s3Data } = await axios.get(
                `${process.env.REACT_APP_API_URL}/api/s3/signed-url?key=${encodeURIComponent(p.imagen)}`,
                { headers: { "auth-token": sessionStorage.getItem("token") } }
              );
              return { ...p, signedUrl: s3Data.url };
            } catch {
              return p;
            }
          })
        );
        setItems(withUrls);
      } catch {
        setItems([]);
      }
    };
    run();
  }, []);

  if (items.length === 0) {
    return null;
  }

  return (
    <div>
      <div className="mb-2 flex items-center justify-between gap-2">
        <h2 className="text-sm font-semibold text-gray-800">Premios destacados</h2>
        <button
          type="button"
          onClick={onVerMas}
          className="shrink-0 text-xs font-semibold text-[#205f43] hover:underline"
        >
          Ver catálogo
        </button>
      </div>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 sm:gap-2.5 lg:grid-cols-4">
        {items.map((p) => (
          <button
            key={p.premio_id}
            type="button"
            onClick={onVerMas}
            className="group flex h-full min-h-0 w-full min-w-0 flex-col overflow-hidden rounded-xl border border-gray-200 bg-white text-left shadow-sm transition hover:border-[#205f43]/45 hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-[#205f43] focus-visible:ring-offset-2"
          >
            <div
              className="relative flex h-[4.5rem] w-full shrink-0 items-center justify-center overflow-hidden sm:h-24"
              style={{ background: "#f0f0f0" }}
            >
              {p.signedUrl ? (
                <img
                  src={p.signedUrl}
                  alt=""
                  className="max-h-[85%] max-w-[90%] object-contain p-0.5"
                />
              ) : (
                <div className="flex h-full w-full items-center justify-center text-[#205f43]/30">
                  <Star className="h-6 w-6" fill="currentColor" strokeWidth={0} />
                </div>
              )}
            </div>
            <div className="flex min-h-0 flex-1 flex-col gap-0.5 p-2 pt-1.5 sm:p-2.5 sm:pt-2">
              <p className="line-clamp-2 text-balance text-[0.7rem] font-semibold leading-tight text-gray-900 sm:text-xs">
                {p.nombre}
              </p>
              {p.puntos != null && (
                <p className="mt-0.5 flex items-center gap-1 text-[0.65rem] font-medium text-[#205f43] sm:text-xs">
                  <Star className="h-2.5 w-2.5 shrink-0 fill-amber-400 text-amber-500 sm:h-3 sm:w-3" />
                  {formatPts(p.puntos)} pts
                </p>
              )}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};

export default TopPremiosTeaser;
